import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { RedirectCell } from "components/common/GridComponents";
import { fetchContactsOnMatter } from "services/contactsService";
import AddNewContact from "../Contacts/AddViewContact";
import AttachContactPage from "pages/AttachContacts/AttachContactPage";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import deleteIcon from "../../assets/common/delete.svg";
import CompanyIcon from "../../assets/Company-Icon.png";
import IndividualIcon from "../../assets/Individual-Icon.png";
import { attachMatterOnContact as detachContactFromMatter } from "../../api";
import { toast } from "react-toastify";
import { GridLoader } from "components/GridLoader";

const CaseContactsPage = (props) => {
  const [contactList, setContactList] = useState([]);
  const [dataState, setDataState] = useState({
    sort: [
      {
        field: "date",
        dir: "asc",
      },
    ],
  });

  const [contactInfo, setContactInfo] = useState({ type: "", contactId: "" });
  const [formView, setFormView] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [detaching, setDetaching] = useState(false);

  const contactsList = useSelector(
    (state) => state.contactsList?.contactsOnMatter?.data
  );
  const isLoading = useSelector((state) => state.contactsList?.isLoading);

  const dispatch = useDispatch();

  const getAllContactsonMatter = async () => {
    dispatch(fetchContactsOnMatter(props.matterId));
  };

  useEffect(() => {
    getAllContactsonMatter();
  }, [props.matterId]);

  useEffect(() => {
    if (props?.refreshClicked && props.activeItem === 5) {
      props?.setRefreshClicked(false);
      getAllContactsonMatter();
    }
  }, [props.refreshClicked]);

  useEffect(() => {
    setContactList(contactsList || []);
  }, [contactsList]);

  const dataStateChange = (e) => {
    setDataState(e.dataState);
  };

  const handleGridClick = (type, contactId) => {
    setContactInfo({ type, contactId });
    setFormView(true);
  };

  const handleDetachContact = (contactId) => {
    setContactToDelete(contactId);
    setShowDeleteConfirm(true);
  };

  const confirmDetachContact = async () => {
    setDetaching(true);
    try {
      await detachContactFromMatter(props.matterId, {
        contactId: contactToDelete,
        active: false,
      });
      await getAllContactsonMatter();
      toast.success("Contact removed from matter successfully");
    } catch (error) {
      console.error("Error detaching contact:", error);
    } finally {
      setShowDeleteConfirm(false);
      setContactToDelete(null);
      setDetaching(false);
    }
  };

  const Redirect = (props) => (
    <RedirectCell
      handleClick={() =>
        handleGridClick(props.dataItem?.type, props?.dataItem?.contactId)
      }
      title="View Contact"
    />
  );

  const DeleteCell = (props) => (
    <td>
      <img
        className="cursor-pointer opacity-75"
        onClick={() => handleDetachContact(props.dataItem?.contactId)}
        src={deleteIcon}
        style={{
          width: "18px",
          height: "18px",
          marginBottom: "0.1rem",
        }}
        onMouseEnter={(e) => e.target.classList.remove("opacity-75")}
        onMouseLeave={(e) => e.target.classList.add("opacity-75")}
        alt="Delete"
        title="Remove Contact from Matter"
      />
    </td>
  );

  const CustomCell = (props) => {
    const item = props.dataItem;
    const references = [item.reference1, item.reference2, item.reference3]
      .filter(Boolean)
      .join(" / ");
    return <td>{references}</td>;
  };

  const CorrespondenceCell = (props) => {
    const item = props.dataItem;
    const line1 = [item.correspondenceFirstName, item.correspondenceEmail]
      .filter(Boolean)
      .join(" - ");
    const line2 = item.correspondencePhone || "";

    return (
      <td>
        {line1 && <div>{line1}</div>}
        {line2 && <div>{line2}</div>}
      </td>
    );
  };

  const TypeIconCell = (props) => {
    const type = props.dataItem?.type?.toLowerCase();
    const icon = type === "company" ? CompanyIcon : IndividualIcon;

    return (
      <td className="d-flex justify-content-center align-items-center">
        <img
          src={icon}
          alt={type}
          title={type}
          className="p-1"
          style={{
            border: "1px solid #d3d3d3",
            borderRadius: "4px",
          }}
        />
      </td>
    );
  };

  return (
    <div className="h-100">
      <Grid
        data={contactList}
        onDataStateChange={dataStateChange}
        sortable={true}
        className="fs-md h-100 overflow-auto"
        showLoader={isLoading}
        loader={<GridLoader />}
      >
        <GridNoRecords>
          {isLoading ? (
            <span></span>
          ) : (
            contactList.data && contactList.data.length === 0 && "No records available"
          )}
        </GridNoRecords>
        <GridColumn width="150" field="name" title="Name" className="fw-bold" />
        <GridColumn width="60" cell={TypeIconCell} title=" " />
        <GridColumn width="150" field="contactCategory" title="Type" />
        <GridColumn width="120" field="phone" title="Phone" />
        <GridColumn width="150" field="email" title="Email" />
        <GridColumn width="120" field="fullAddress" title="Address" />
        <GridColumn width="200" cell={CorrespondenceCell} title="Correspondence" />
        <GridColumn width="120" cell={CustomCell} title="References" />
        <GridColumn width="30" cell={Redirect} />
        <GridColumn width="44" cell={DeleteCell} align="center" />
      </Grid>

      {formView && (
        <AddNewContact
          attachContact={true}
          type={contactInfo.type}
          viewMode={true}
          contactId={contactInfo.contactId}
          close={() => setFormView(false)}
        />
      )}

      {props.attachContact && (
        <AttachContactPage
          contactsData={getAllContactsonMatter}
          visible={props.attachContact}
          matterId={props?.matterId}
          toggleDialog={() => props?.setAttachContact(!props?.attachContact)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmationDialog
          content="Are you sure you want to remove this contact from the matter?"
          onClickYes={confirmDetachContact}
          onClickNo={() => {
            setShowDeleteConfirm(false);
            setContactToDelete(null);
          }}
          onClose={() => {
            setShowDeleteConfirm(false);
            setContactToDelete(null);
          }}
          isLoading={detaching}
        />
      )}

    </div>
  );
};

export default CaseContactsPage;
